import React from 'react'
import { StandardColumn } from '../../../../components/StandardPage'
import { OverflowCell } from '../../../../components/OverflowCell'

export const certificatesScreenKey = 'certificates'

export interface TestItem {
  id?: number | string
  name: string
  value: string
  unit?: string
}

export interface CertificateRow {
  id: number
  param: string
  supplier: string
  customer: string
  heatNumber: string
  type: { id: number; name: string }
  supplierIsMain: boolean
  date: string
  codeOpString: string
  startingMaterial?: string
  description?: string
  invoiceNumber?: string
  standard?: string
  heatTreatment?: string
  purchaseOrderNumber?: string
  factory?: string
  observations?: string
  mechanicalTests?: TestItem[]
  chemicalComposition?: TestItem[]
}

const resolveTypeLabel = (row: CertificateRow): string => {
  if (row.type?.id === 1 && row.supplierIsMain) return 'Qualidade'
  if (row.type?.id === 1) return 'Origem'
  return row.type?.name || '-'
}

const formatTestItemsText = (items?: TestItem[]): string => {
  if (!items || items.length === 0) return 'Nenhum'
  return items
    .map((item) => `${item.name}: ${item.value}${item.unit ? ` ${item.unit}` : ''}`)
    .join(', ')
}

const renderTestItemsCell = (items?: TestItem[]) => {
  if (!items || items.length === 0) return 'Nenhum'
  const inlineText = items
    .map((item) => `${item.name}: ${item.value}${item.unit ? ` ${item.unit}` : ''}`)
    .join(', ')
  const tooltipText = items
    .map((item) => `${item.name}: ${item.value}${item.unit ? ` ${item.unit}` : ''}`)
    .join('\n')

  return React.createElement(
    OverflowCell,
    { text: tooltipText, maxWidth: 200, forceTooltip: items.length > 1 },
    inlineText
  )
}

const renderOverflow = (text: string, maxWidth = 200) => {
  return React.createElement(OverflowCell, { text, maxWidth }, text)
}

export const buildCertificatesColumns = (): StandardColumn<CertificateRow>[] => [
  {
    id: 'param',
    label: 'Parâmetro',
    accessor: (row) => row.param || '-',
    cell: (row) => renderOverflow(row.param || '-', 200),
  },
  {
    id: 'supplier',
    label: 'Fornecedor',
    accessor: (row) => row.supplier || '-',
    cell: (row) => renderOverflow(row.supplier || '-', 200),
  },
  {
    id: 'customer',
    label: 'Cliente',
    accessor: (row) => row.customer || '-',
    cell: (row) => renderOverflow(row.customer || '-', 200),
  },
  {
    id: 'type',
    label: 'Tipo do certificado',
    accessor: (row) => resolveTypeLabel(row),
    cell: (row) => renderOverflow(resolveTypeLabel(row), 150),
  },
  {
    id: 'heatNumber',
    label: 'Nº da corrida',
    accessor: (row) => row.heatNumber || '-',
    cell: (row) => renderOverflow(row.heatNumber || '-', 150),
  },
  {
    id: 'date',
    label: 'Data de criação',
    accessor: (row) => row.date || '-',
  },
  {
    id: 'codeOpString',
    label: 'Ordem de produção',
    accessor: (row) => row.codeOpString || '-',
    cell: (row) => renderOverflow(row.codeOpString || '-', 150),
  },
  {
    id: 'startingMaterial',
    label: 'Material de partida',
    accessor: (row) => row.startingMaterial || '-',
    cell: (row) => renderOverflow(row.startingMaterial || '-', 200),
  },
  {
    id: 'description',
    label: 'Descrição',
    accessor: (row) => row.description || '-',
    cell: (row) => renderOverflow(row.description || '-', 200),
  },
  {
    id: 'invoiceNumber',
    label: 'Nota fiscal',
    accessor: (row) => row.invoiceNumber || '-',
    cell: (row) => renderOverflow(row.invoiceNumber || '-', 150),
  },
  {
    id: 'standard',
    label: 'Norma',
    accessor: (row) => row.standard || '-',
    cell: (row) => renderOverflow(row.standard || '-', 200),
  },
  {
    id: 'heatTreatment',
    label: 'Tratamento térmico',
    accessor: (row) => row.heatTreatment || '-',
    cell: (row) => renderOverflow(row.heatTreatment || '-', 200),
  },
  {
    id: 'purchaseOrderNumber',
    label: 'Nº Pedido de compra do cliente',
    accessor: (row) => row.purchaseOrderNumber || '-',
    cell: (row) => renderOverflow(row.purchaseOrderNumber || '-', 200),
  },
  {
    id: 'factory',
    label: 'Usina de fabricação',
    accessor: (row) => row.factory || '-',
    cell: (row) => renderOverflow(row.factory || '-', 200),
  },
  {
    id: 'observations',
    label: 'Observações',
    accessor: (row) => row.observations || '-',
    cell: (row) => renderOverflow(row.observations || '-', 200),
  },
  {
    id: 'mechanicalTests',
    label: 'Ensaios mecânicos',
    sortable: false,
    disableBulkEdit: true,
    accessor: (row) => formatTestItemsText(row.mechanicalTests),
    cell: (row) => renderTestItemsCell(row.mechanicalTests),
    exportValue: (row) => formatTestItemsText(row.mechanicalTests),
  },
  {
    id: 'chemicalComposition',
    label: 'Composição química',
    sortable: false,
    disableBulkEdit: true,
    accessor: (row) => formatTestItemsText(row.chemicalComposition),
    cell: (row) => renderTestItemsCell(row.chemicalComposition),
    exportValue: (row) => formatTestItemsText(row.chemicalComposition),
  },
]
